/**
 * Seam v1 — the framework-free plugin contract (PoC).
 *
 * Design: todo/2026-07-14_note3-plugin-system/seam-design.md
 * The seam's reactive currency is the wovin Subscribable — live-query
 * reactivity is framework-neutral; adapters (Solid here, Vue symmetric)
 * wire it into framework reactivity.
 *
 * NOTE: note3-only until stable-ish, but written as-if-extractable:
 * this file must not import from the app — only @wovin/core types.
 */
import type { ApplogValue, EntityID, Thread } from '@wovin/core'
import type { DatalogQueryPattern } from '@wovin/core/applog'

export const SEAM_API_VERSION = 1

/** Prop values: plain JSON | callbacks | wovin Subscribables */
export type SeamProps = Record<string, unknown>

export interface SeamMountHandle {
	update?: (props: SeamProps) => void
	dispose: () => void
}

export interface SeamComponent {
	name: string
	/**
	 * 'shadow' (default): mounted into an open shadow root — style containment.
	 * 'none': mounted into a plain div — for adapter-wrapped app-native components.
	 */
	styleIsolation?: 'shadow' | 'none'
	mount: (el: HTMLElement, props: SeamProps, ctx: PluginContext) => SeamMountHandle
}

/** The `kids` slot (v1 PoC) — other slots follow the same shape later. */
export interface SeamKidRenderer {
	/** block.type dispatch key (pattern-bindings come later) */
	type: string
	displayName: string
	/** iconify name — the host renders the toolbar icon, plugins ship no icon components */
	icon?: string
	/** receives props: { blockID, items: {id, relationID, blockID}[] } */
	root: SeamComponent
}

/**
 * Capability identifiers a plugin declares in `requires`. Deliberately strings,
 * not a version number — decentralized, composable, and later the user-consent
 * surface ("this plugin wants write access — allow?"). (manu, round-6 review)
 */
export const HOST_CAPABILITIES = [
	'wovin:read',
	'wovin:write',
	'ui:solid',
	'ui:render',
	'editor:tiptap',
	'roots:provide',
] as const
export type HostCapability = typeof HOST_CAPABILITIES[number]

export interface RootDescriptor {
	id: EntityID
	/** not a note3 block — the entity slot handles rendering */
	virtual?: boolean
	/** for list-style providers (e.g. smartlist groups) */
	title?: string
}

/** Contributes (virtual) roots to the outline — the query-power surface. */
export interface RootProvider {
	name: string
	/** cheap declarative form: entities carrying these attrs become roots */
	patterns?: DatalogQueryPattern[]
	/** full-power form (smartlist-class): reactive accessor of descriptors */
	provide?: (wovin: SeamWovinApi) => () => readonly RootDescriptor[]
}

/** Contributes tiptap editor extensions (inline decorations etc.) */
export interface EditorExtensionProvider {
	name: string
	/** build tiptap extensions using the classes from ctx.editor */
	extensions: (ctx: PluginContext) => unknown[]
}

/**
 * What a plugin provides — mounting a component in a slot (kidRenderers) is
 * ONE provider kind among several; the umbrella is the plugin.
 */
export interface PluginProvides {
	kidRenderers?: SeamKidRenderer[]
	rootProviders?: RootProvider[]
	editorExtensions?: EditorExtensionProvider[]
	hiddenExtraPropPrefixes?: () => string[]
	// later: settings, bulletRenderers, block-widgets, entity renderers …
}

export interface PluginDef {
	name: string
	/** capability identifiers this plugin needs — see HOST_CAPABILITIES */
	requires?: string[]
	provides?: PluginProvides
	/** @deprecated PoC shape — use provides.kidRenderers */
	kidRenderers?: SeamKidRenderer[]
	/** @deprecated centralized version number — declare `requires` capabilities instead */
	requiresApi?: number
}

/** Shape of a plugin module: `export function loadPlugin(ctx) { ... }` */
export interface SeamPluginModule {
	loadPlugin: (ctx: PluginContext) => PluginDef | Promise<PluginDef>
}

/** Structural view of a core QueryNode (avoids deep type imports for plugins) */
export interface SeamQueryNode {
	variables: Record<string, ApplogValue | EntityID>
}

export interface SeamWovinApi {
	/** Current display thread (lastWriteWins + withoutDeleted applied) */
	thread: () => Thread
	/** Advanced: the unfiltered display thread (needed by app-context re-entry) */
	rawThread: () => Thread
	query: (patterns: DatalogQueryPattern[]) => { records: ReadonlyArray<Record<string, ApplogValue>> }
	liveQuery: (patterns: DatalogQueryPattern[]) => unknown
	liveFilterAndMap: (pattern: DatalogQueryPattern, mapper: unknown) => unknown
	/**
	 * liveQuery bridged to a reactive accessor. Must be called during mount /
	 * inside the component's reactive root (cleanup is tied to it).
	 */
	useLive: (patterns: DatalogQueryPattern[]) => () => readonly SeamQueryNode[]
	/** Writes on behalf of the user (their `ag`); throws on read-only threads. */
	insertApplogs: (applogs: object | object[]) => unknown
	contentToPlaintext: (vl: ApplogValue) => string
}

export interface SeamUiApi {
	/** solid-js/html tagged template — the no-build authoring path */
	html: (strings: TemplateStringsArray, ...args: unknown[]) => unknown
	/** createSignal, createMemo, createEffect, onCleanup, For, Show, getOwner */
	solid: Record<string, unknown>
	/** wovin SubscribableArray → reactive accessor (hooks-solid) */
	useSubscribable: (source: unknown) => () => readonly unknown[]
	/** Blessed adapter: author with Solid reactivity, get a SeamComponent */
	defineSolidComponent: (
		name: string,
		fn: (props: SeamProps, ctx: PluginContext) => unknown,
		opts?: { styleIsolation?: 'shadow' | 'none' },
	) => SeamComponent
	/** Recursive dispatch: render any entity with note3's own machinery. Returns dispose. */
	renderEntity: (el: HTMLElement, entityID: EntityID, opts?: Record<string, unknown>) => () => void
}

export interface PluginContext {
	wovin: SeamWovinApi
	ui: SeamUiApi
	/** tiptap/ProseMirror classes for editorExtensions providers ('editor:tiptap') */
	editor?: {
		Tiptap: Record<string, unknown>
		TiptapView: Record<string, unknown>
		ProseMirror: Record<string, unknown>
	}
	meta: {
		/** where the code was fetched from — NOT the identity */
		pluginUrl?: string
		/** content-derived plugin identity (deterministic entity ID of the source) */
		pluginId?: string
		apiVersion: number
		readOnly: () => boolean
	}
}
